import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { RichFieldComponent } from '../../../../../shared/math/rich-field/rich-field.component';

/**
 * Modes de saisie des réponses « à choix ».
 * - `single`     : boutons radio, exactement une bonne réponse
 * - `multiple`   : cases à cocher, au moins une bonne réponse
 * - `true_false` : deux libellés générés et non modifiables
 */
export type ChoiceMode = 'single' | 'multiple' | 'true_false';

export interface ChoiceOptionValue {
  content: string;
  isCorrect: boolean;
}

export type ChoiceOptionGroup = FormGroup<{
  content: FormControl<string>;
  isCorrect: FormControl<boolean>;
}>;

export type ChoiceAnswersForm = FormGroup<{
  options: FormArray<ChoiceOptionGroup>;
}>;

/**
 * Éditeur de réponses à choix (single_choice / multiple_choice / true_false).
 *
 * Composant purement présentationnel : il ne construit ni ne mute la structure
 * du formulaire, il la reçoit via `@Input() group` et remonte les intentions de
 * l'utilisateur (ajout / suppression / sélection de la bonne réponse) au parent
 * via des `@Output()`. Aucun accès au DOM.
 */
@Component({
  selector: 'app-choice-answers',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, RichFieldComponent],
  templateUrl: './choice-answers.component.html',
  styleUrl: './choice-answers.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChoiceAnswersComponent {
  /** Sous-formulaire des réponses, détenu et construit par le parent. */
  @Input({ required: true }) group!: ChoiceAnswersForm;

  /** Mode de saisie — pilote radio vs cases à cocher. */
  @Input({ required: true }) mode!: ChoiceMode;

  /** Nombre minimum de réponses exigé par le type courant. */
  @Input() minOptions = 2;

  /** Nombre maximum de réponses autorisé. */
  @Input() maxOptions = 10;

  /** L'utilisateur demande une réponse supplémentaire. */
  @Output() addOption = new EventEmitter<void>();

  /** L'utilisateur demande la suppression de la réponse d'index donné. */
  @Output() removeOption = new EventEmitter<number>();

  /** L'utilisateur désigne la réponse d'index donné comme la bonne (mode exclusif). */
  @Output() correctSelected = new EventEmitter<number>();

  get options(): FormArray<ChoiceOptionGroup> {
    return this.group.controls.options;
  }

  /** Les libellés sont générés et verrouillés en vrai/faux uniquement. */
  get isLocked(): boolean {
    return this.mode === 'true_false';
  }

  /** Vrai/faux et choix unique n'acceptent qu'une seule bonne réponse. */
  get isExclusive(): boolean {
    return this.mode !== 'multiple';
  }

  get canAdd(): boolean {
    return !this.isLocked && this.options.length < this.maxOptions;
  }

  get canRemove(): boolean {
    return !this.isLocked && this.options.length > this.minOptions;
  }

  /** Libellé i18n figé des options vrai/faux (le contenu stocké est `true` / `false`). */
  lockedLabelKey(index: number): string {
    return index === 0
      ? 'QUESTION_EDITOR.TRUE_LABEL'
      : 'QUESTION_EDITOR.FALSE_LABEL';
  }

  optionLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }
}
